import { hasTildeSupport, toGameText } from '../firered/charset';
import type { PlayerSprites } from '../firered/sprites';

/** VAR_STARTER_MON do jogo: 0 BULBASAUR, 1 SQUIRTLE, 2 CHARMANDER. */
export type StarterChoice = 0 | 1 | 2;
const STARTER_NAMES: Record<StarterChoice, string> = { 0: 'BULBASAUR', 1: 'SQUIRTLE', 2: 'CHARMANDER' };

const NAME_MAX = 7;
const RIVAL = 'GARY';
const WALK_FRAMES = [3, 0, 4, 0]; // andando para baixo
const WALK_MS = 170;

export interface NewGameChoice {
  name: string;
  rival: string;
  gender: 0 | 1;
  /** null = a ROM não sabe entregar o inicial (aí é o caminho normal, com o professor). */
  starter: StarterChoice | null;
}

const $ = <T extends HTMLElement>(root: ParentNode, selector: string): T => {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Elemento não encontrado: ${selector}`);
  return element;
};

/**
 * Tela "Crie seu treinador": substitui a conversa inicial do Prof. Carvalho.
 * Funciona com teclado físico (Tab, setas para o gênero, Enter para começar) e no celular.
 */
export class NewGameScreen {
  private readonly form: HTMLFormElement;
  private readonly nameInput: HTMLInputElement;
  private readonly preview: HTMLElement;
  private readonly count: HTMLElement;
  private sprites: PlayerSprites | null = null;
  private starterSupport = false;
  private animation: number | undefined;
  private step = 0;

  constructor(private readonly root: HTMLElement) {
    this.form = $(root, 'form');
    this.nameInput = $(root, 'input[name="name"]');
    this.preview = $(root, '.dialog-preview p');
    this.count = $(root, '.count');

    this.nameInput.maxLength = NAME_MAX;
    this.nameInput.addEventListener('input', () => {
      // Só o que o jogo consegue escrever (emojis somem; ã/õ só na nossa ROM)
      const clean = toGameText(this.nameInput.value, NAME_MAX);
      if (clean !== this.nameInput.value) this.nameInput.value = clean;
      this.update();
    });
    this.form.addEventListener('change', () => this.update());
  }

  setSprites(sprites: PlayerSprites | null): void {
    this.sprites = sprites;
  }

  /** Mostra a escolha do inicial (só na nossa ROM) com a foto de cada um. */
  setStarters(frontPics: Map<number, HTMLCanvasElement> | null): void {
    this.starterSupport = frontPics !== null;
    $(this.root, '.starters').hidden = !this.starterSupport;
    if (!frontPics) return;
    for (const canvas of this.root.querySelectorAll<HTMLCanvasElement>('canvas[data-species]')) {
      const pic = frontPics.get(Number(canvas.dataset.species));
      if (pic) canvas.getContext('2d')!.drawImage(pic, 0, 0);
    }
  }

  /** Mostra a tela e resolve quando o jogador confirmar. */
  ask(defaultName: string): Promise<NewGameChoice> {
    this.nameInput.value = toGameText(defaultName, NAME_MAX);
    $(this.root, '.note').textContent = hasTildeSupport()
      ? 'Até 7 letras. Acentos e til funcionam.'
      : 'Até 7 letras. O jogo ainda não tem til: ã e õ viram a e o.';
    this.root.hidden = false;
    this.update();
    this.animation = window.setInterval(() => this.drawSprites(++this.step), WALK_MS);
    if (matchMedia('(pointer: fine)').matches) this.nameInput.focus();

    return new Promise((resolve) => {
      const submit = (event: SubmitEvent) => {
        event.preventDefault();
        const name = this.nameInput.value.trim();
        if (!name) {
          this.nameInput.focus();
          this.nameInput.classList.add('shake');
          setTimeout(() => this.nameInput.classList.remove('shake'), 400);
          return;
        }
        this.form.removeEventListener('submit', submit);
        window.clearInterval(this.animation);
        this.nameInput.blur();
        this.root.hidden = true;
        resolve({ name, rival: RIVAL, gender: this.gender, starter: this.starter });
      };
      this.form.addEventListener('submit', submit);
    });
  }

  private get starter(): StarterChoice | null {
    if (!this.starterSupport) return null;
    const value = this.form.querySelector<HTMLInputElement>('input[name="starter"]:checked')?.value;
    return value === '0' || value === '1' ? (Number(value) as StarterChoice) : 2;
  }

  private get gender(): 0 | 1 {
    return this.form.querySelector<HTMLInputElement>('input[name="gender"]:checked')?.value === '1' ? 1 : 0;
  }

  private update(): void {
    const name = this.nameInput.value.trim() || '…';
    const rival = RIVAL;
    this.count.textContent = `${this.nameInput.value.length}/${NAME_MAX}`;
    const starter = this.starter;
    this.preview.replaceChildren(
      'Então você é ',
      bold(name),
      '! ',
      ...(starter === null ? [] : ['Seu parceiro é ', bold(STARTER_NAMES[starter]), '. ']),
      'Seu rival, ',
      bold(rival),
      ', já está te esperando!',
    );
    this.drawSprites(this.step);
  }

  private drawSprites(step: number): void {
    if (!this.sprites) return;
    for (const canvas of this.root.querySelectorAll<HTMLCanvasElement>('canvas[data-gender]')) {
      const gender = Number(canvas.dataset.gender) as 0 | 1;
      const frames = this.sprites.byGender[gender];
      const frame = gender === this.gender ? WALK_FRAMES[step % WALK_FRAMES.length] : 0;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(frames[frame], 0, 0);
    }
  }
}

function bold(text: string): HTMLElement {
  const element = document.createElement('strong');
  element.textContent = text;
  return element;
}
